// useActiveStore — which store the user is currently shopping at.
//
// Set by Shop Mode's trip-start store pick, cleared when the trip
// commits or cancels. Brand pickers read it (hook or imperative) to
// boost the store's house brands — "at Costco, Kirkland first".
// Null outside a trip, so every consumer degrades to today's
// behavior with zero conditionals at the call site.
//
// Exact clone of the useShoppingRegion pattern: localStorage value,
// same-tab listener broadcast, cross-tab storage events, imperative
// getter for non-React callers. A second key remembers the LAST
// store so the next trip's pre-screen can pre-highlight it.
//
// Storage-vs-display rule: the store string is stored VERBATIM as
// the user picked/typed it. Matching against curated brand records
// normalizes both sides at compare time (storeNamesMatch in
// brandExpertise.js) — never rewrite the stored value.

import { useEffect, useState, useCallback } from "react";

const ACTIVE_KEY = "mise:active_store:v1";
const LAST_KEY   = "mise:last_store:v1";

const listeners = new Set();
function broadcast(value) {
  for (const fn of listeners) {
    try { fn(value); } catch (_e) { /* listener threw, drop it */ }
  }
}

function readStored(key) {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage?.getItem(key) || null;
  } catch { return null; }
}

export function useActiveStore() {
  const [store, setStore] = useState(() => readStored(ACTIVE_KEY));

  useEffect(() => {
    const fn = (next) => setStore(next);
    listeners.add(fn);
    const onStorage = (e) => {
      if (e.key === ACTIVE_KEY) setStore(e.newValue || null);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("storage", onStorage);
    }
    return () => {
      listeners.delete(fn);
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", onStorage);
      }
    };
  }, []);

  const update = useCallback((next) => {
    const value = (typeof next === "string" && next.trim()) || null;
    if (typeof window !== "undefined") {
      try {
        if (value) {
          window.localStorage?.setItem(ACTIVE_KEY, value);
          // Last-store memory only advances on a real pick — a
          // clear (trip end) must not erase it.
          window.localStorage?.setItem(LAST_KEY, value);
        } else {
          window.localStorage?.removeItem(ACTIVE_KEY);
        }
      } catch { /* quota / private mode */ }
    }
    setStore(value);
    broadcast(value);
  }, []);

  return [store, update];
}

// Imperative read for non-React callers (catalog matchers inside
// pipelines, commit paths).
export function getActiveStore() {
  return readStored(ACTIVE_KEY);
}

// The most recent store the user shopped at — pre-highlighted on
// the next trip's store row.
export function getLastStore() {
  return readStored(LAST_KEY);
}

// Imperative setters for trip lifecycle code (useShopTrip start /
// commit / cancel) where the hook form doesn't fit.
export function setActiveStore(value) {
  const v = (typeof value === "string" && value.trim()) || null;
  if (typeof window !== "undefined") {
    try {
      if (v) {
        window.localStorage?.setItem(ACTIVE_KEY, v);
        window.localStorage?.setItem(LAST_KEY, v);
      } else {
        window.localStorage?.removeItem(ACTIVE_KEY);
      }
    } catch { /* noop */ }
  }
  broadcast(v);
}
